/**
 * OnboardingSlides - 첫 방문 온보딩 슬라이드
 *
 * WIA Book의 핵심 기능을 담백하게 소개
 * 첫 방문 시 한 번만 표시 (localStorage로 체크)
 *
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { useState, useEffect } from 'react';
import { useI18n } from '../../i18n';
import { FiChevronRight, FiChevronLeft, FiX } from 'react-icons/fi';

interface OnboardingSlidesProps {
  onComplete: () => void;
}

const OnboardingSlides = ({ onComplete }: OnboardingSlidesProps) => {
  const { t } = useI18n();
  const [currentSlide, setCurrentSlide] = useState(0);

  // 슬라이드 데이터 - 핵심 기능만 담백하게
  const slides = [
    {
      icon: '📚',
      titleKey: 'onboarding.slide1.title',
      titleFallback: 'PDF + EPUB + Flipbook 통합',
      descKey: 'onboarding.slide1.desc',
      descFallback: '세 가지 형식을 하나의 뷰어에서. 세계 최초 통합 전자책 플랫폼입니다.',
      color: 'from-blue-500 to-purple-600',
    },
    {
      icon: '🎬',
      titleKey: 'onboarding.slide2.title',
      titleFallback: '실감나는 3D 플립북',
      descKey: 'onboarding.slide2.desc',
      descFallback: '6가지 페이지 효과와 사실적인 책 넘김 소리. 진짜 책을 읽는 느낌을 그대로.',
      color: 'from-pink-500 to-rose-600',
    },
    {
      icon: '⚙️',
      titleKey: 'onboarding.slide3.title',
      titleFallback: '나만의 독서 환경',
      descKey: 'onboarding.slide3.desc',
      descFallback: '12가지 테마, 5가지 글꼴, 글자 크기와 줄간격까지. 당신에게 맞는 설정을 찾으세요.',
      color: 'from-amber-500 to-orange-600',
    },
    {
      icon: '🔊',
      titleKey: 'onboarding.slide4.title',
      titleFallback: '소리내어 읽어드립니다',
      descKey: 'onboarding.slide4.desc',
      descFallback: 'TTS로 책을 들으세요. 배경 음악과 함께 더 몰입감 있는 독서 경험을.',
      color: 'from-green-500 to-teal-600',
    },
    {
      icon: '💬',
      titleKey: 'onboarding.slide5.title',
      titleFallback: '기록하고 기억하세요',
      descKey: 'onboarding.slide5.desc',
      descFallback: '하이라이트, 메모, 북마크. 중요한 내용을 표시하고 언제든 돌아오세요.',
      color: 'from-cyan-500 to-blue-600',
    },
    {
      icon: '🌍',
      titleKey: 'onboarding.slide6.title',
      titleFallback: '211개 언어, 모든 기기',
      descKey: 'onboarding.slide6.desc',
      descFallback: '전 세계 어디서나, 어떤 기기에서나. QR 코드로 간편하게 동기화하세요.',
      color: 'from-violet-500 to-purple-600',
    },
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('wia-onboarding-completed', 'true');
    onComplete();
  };

  const handleSkip = () => {
    handleComplete();
  };

  const slide = slides[currentSlide];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center pt-[200px] z-[100] p-4">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Skip Button */}
        <div className="flex justify-end p-4">
          <button
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex items-center gap-1 text-sm"
          >
            {t('onboarding.skip') || '건너뛰기'}
            <FiX size={16} />
          </button>
        </div>

        {/* Slide Content */}
        <div className="px-8 pb-8">
          {/* Icon */}
          <div className={`w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br ${slide.color} flex items-center justify-center shadow-lg`}>
            <span className="text-5xl">{slide.icon}</span>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-4">
            {t(slide.titleKey) || slide.titleFallback}
          </h2>

          {/* Description */}
          <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed mb-8">
            {t(slide.descKey) || slide.descFallback}
          </p>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mb-8">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-primary w-8'
                    : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentSlide === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                currentSlide === 0
                  ? 'text-gray-300 dark:text-gray-700 cursor-not-allowed'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <FiChevronLeft size={20} />
              {t('onboarding.prev') || '이전'}
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-purple-600 text-white rounded-xl font-medium hover:shadow-lg transition-all hover:scale-105"
            >
              {currentSlide === slides.length - 1
                ? t('onboarding.start') || '시작하기'
                : t('onboarding.next') || '다음'}
              <FiChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// 온보딩 표시 여부 확인 훅
export const useOnboarding = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('wia-onboarding-completed');
    if (!completed) {
      setShowOnboarding(true);
    }
  }, []);

  const completeOnboarding = () => {
    setShowOnboarding(false);
  };

  const resetOnboarding = () => {
    localStorage.removeItem('wia-onboarding-completed');
    setShowOnboarding(true);
  };

  return { showOnboarding, completeOnboarding, resetOnboarding };
};

export default OnboardingSlides;
